import React from 'react';
import {
  ConfirmationTypes,
  IPharmacistDetails,
  approveLongTermContract,
  approveRequest,
  getLongTermBookingsById,
  getTypeOfUser,
  ignoreRequest,
  longTermContractRequestDetails,
  pharmacistBookingDetails,
  pharmacyPreApprove,
  rejectLongTermContract,
  selectPharmacyPreApprove,
} from '@pharmaplan/common';
import { idStore } from '@pharmaplan/common/helpers/ServiceHandler';
import OutcomeModal from '../../components/Modals/OutcomeModal';
import { setDialog } from '../../reducers/dialogReducer';
import { useAppDispatch } from '../useAppDispatch';
import { OutcomeModalTypes, ScreenTypes } from '../../helpers/Constants';
import strings from '../../localization';
import { useAppSelector } from '../useAppSelector';
import { renderScreen } from '../../actions/drawerActions';
import { IPharmacistProfile } from '../../components/SelfService/PharmacistProfile/PharmacistProfile';
import ConfirmationModal from '../../components/Modals/ConfirmationModal';
import SoftwareWarning from '../../components/SelfService/ServiceWorkshiftDetails/SoftwareWarning';
import ViewLongTermBookings from '../../components/SelfService/ViewLongTermBookings';

const { bookingPending } = OutcomeModalTypes;
const { workshiftSummary } = ScreenTypes;
const { counterOfferCancel, counterOfferConfirm } = ConfirmationTypes;

const usePharmacistProfile = ({
  date,
  type,
  eventId,
  eventIds,
  workshiftId,
  professionalId,
  preventInteraction,
  longTermContractId,
}: Partial<IPharmacistProfile>) => {
  const dispatch = useAppDispatch();

  const bookingDetails = useAppSelector(pharmacistBookingDetails);
  const longTermDetails = useAppSelector(longTermContractRequestDetails);

  const details = longTermContractId ? longTermDetails : bookingDetails;
  const preRequest = useAppSelector(selectPharmacyPreApprove);

  const { bookingState } = (details as IPharmacistDetails) ?? {};
  const { softwareLevel, name } = preRequest ?? {};

  const userType = useAppSelector(getTypeOfUser);

  const approveAction = () => {
    if (longTermContractId) {
      dispatch(
        approveLongTermContract({
          professionalId: professionalId ?? '',
          longTermContractId,
        }),
      );
    } else {
      dispatch(approveRequest({ userType, bookingId: eventId ?? '' }));
    }
  };

  const ignoreAction = () => {
    if (longTermContractId) {
      dispatch(
        rejectLongTermContract({
          longTermContractId: longTermDetails?.longTermContractId,
          professionalId: longTermDetails?.professionalId,
        }),
      );
    } else {
      dispatch(ignoreRequest({ userType, bookingId: eventId ?? '' }));
    }
  };

  const handleSoftwareWarning = () => {
    dispatch(pharmacyPreApprove((longTermDetails?.bookingId || eventId) ?? ''));
  };

  const softwarePercentage = (softwareLevel - 1) * 25;
  const softwareWarningMsg = strings.formatString(
    strings.pharmacySoftwareWarning,
    name,
    `${softwarePercentage}`,
  );

  const gotoRequestsList = () => {
    dispatch(
      renderScreen({
        eventId: workshiftId,
        eventIds,
        date,
        type,
        screenNumber: 2,
        screenType: workshiftSummary,
        extraParameters: {
          longTermShiftContractId: longTermContractId,
        },
      }),
    );
  };

  const gotoWorkshiftDetails = () => {
    dispatch(
      renderScreen({
        eventIds,
        type,
        bookingState,
        screenNumber: 5,
        date,
        screenType: workshiftSummary,
      }),
    );
  };

  const counterOfferConfirmation = () => {
    dispatch(
      setDialog({
        showCloseButton: false,
        Component: (
          <ConfirmationModal
            type={counterOfferConfirm}
            confirmAction={handleSoftwareWarning}
          />
        ),
        heading: {
          title: '',
        },
      }),
    );
  };

  const counterOfferCancellation = () => {
    dispatch(
      setDialog({
        showCloseButton: false,
        Component: (
          <ConfirmationModal
            type={counterOfferCancel}
            confirmAction={ignoreAction}
          />
        ),
        heading: {
          title: '',
        },
      }),
    );
  };

  const showApproveSuccessModal = () => {
    dispatch(
      setDialog({
        heading: {
          title: '',
        },
        Component: (
          <OutcomeModal
            type={bookingPending}
            showCloseButton
            message={strings.confirmedBookings}
          />
        ),
        showCloseButton: false,
      }),
    );
  };

  const showSoftwareWarning = () => {
    dispatch(
      setDialog({
        heading: {
          title: '',
        },
        Component: (
          <ConfirmationModal
            type={ConfirmationTypes.softwareWarning}
            customMessage={<SoftwareWarning message={softwareWarningMsg} />}
            showCloseButton
            confirmAction={approveAction}
          />
        ),
        showCloseButton: false,
      }),
    );
  };

  const getEvent = () => {
    dispatch(
      getLongTermBookingsById({
        longTermContractId: longTermContractId ?? '',
        professionalId: professionalId ?? '',
      }),
    );
  };

  const goToBookings = () => {
    dispatch(
      setDialog({
        heading: {
          title: strings.bookingDetails,
        },
        Component: (
          <ViewLongTermBookings getEvent={getEvent} />
        ),
        maxWidth: 'lg',
        showCloseButton: true,
      }),
    );
  };

  const comeBackFromRating = () => {
    dispatch(
      renderScreen({
        eventId,
        workshiftId: eventId,
        date,
        eventIds,
        type,
        screenNumber: 3,
        pharmacistId: professionalId,
        screenType: ScreenTypes.workshiftSummary,
        extraParameters: {
          preventInteraction,
          longTermShiftContractId: longTermContractId,
        },
      }),
    );
  };
  return {
    gotoRequestsList,
    approveAction,
    ignoreAction,
    gotoWorkshiftDetails,
    showApproveSuccessModal,
    counterOfferCancellation,
    counterOfferConfirmation,
    handleSoftwareWarning,
    showSoftwareWarning,
    goToBookings,
    comeBackFromRating,
  };
};

export default usePharmacistProfile;
